import React, { useEffect, useState, useCallback } from 'react';
import { createApiClient } from '../lib/apiClient';
import { Loader2, DollarSign, Truck, Info, CheckCircle2, Mail, Check, Ban, ChevronDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = createApiClient();
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BLUE = '#002FA7';

const TaxReimbursementsTab = ({ isAdmin }) => {
  const [data, setData] = useState(null);
  const [payrollId, setPayrollId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [digestMsg, setDigestMsg] = useState('');
  const [denied, setDenied] = useState([]);
  const [showDenied, setShowDenied] = useState(false);

  const load = useCallback(async () => {
    const [r, d] = await Promise.all([
      ax.get('/tax/reimbursements'),
      ax.get('/tax/reimbursements/denied').catch(() => ({ data: [] })),
    ]);
    setData(r.data);
    setDenied(d.data || []);
    if (r.data.payrolls?.length && !payrollId) setPayrollId(r.data.payrolls[0].id);
  }, [payrollId]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runDigest = async () => {
    setDigestMsg('Sending…');
    try {
      const r = await ax.post('/tax/reimbursements/digest/run');
      setDigestMsg(r.data.sent > 0
        ? `Recap emailed to ${r.data.sent} driver(s).`
        : `Prepared recaps for ${r.data.drivers?.length || 0} driver(s) — none delivered (check email settings).`);
    } catch (e) {
      setDigestMsg(e.response?.data?.detail || 'Could not send digest.');
    }
    setTimeout(() => setDigestMsg(''), 6000);
  };

  const pay = async () => {
    if (!payrollId) return;
    setBusy(true); setResult(null);
    try {
      const r = await ax.post('/tax/reimbursements/pay', { payroll_id: payrollId });
      setResult(r.data);
      await load();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Could not process reimbursements.' });
    } finally { setBusy(false); }
  };

  const decide = async (eid, action) => {
    let body = {};
    if (action === 'deny') {
      const reason = window.prompt('Reason for denying (optional, shown to the driver):', '');
      if (reason === null) return;
      body = { reason };
    }
    try { await ax.post(`/tax/reimbursements/${eid}/${action}`, body); await load(); }
    catch (e) { setResult({ error: e.response?.data?.detail || 'Could not update.' }); }
  };

  const approveDriver = async (driverId) => {
    try { const r = await ax.post('/tax/reimbursements/approve-driver', { driver_id: driverId }); setResult({ ok: `Approved ${r.data.approved} receipt(s).` }); await load(); }
    catch (e) { setResult({ error: e.response?.data?.detail || 'Could not approve.' }); }
  };

  const denyDriver = async (driverId) => {
    const reason = window.prompt('Deny all for this driver — reason (optional, shown to the driver):', '');
    if (reason === null) return;
    try { const r = await ax.post('/tax/reimbursements/deny-driver', { driver_id: driverId, reason }); setResult({ ok: `Denied ${r.data.denied} receipt(s).` }); await load(); }
    catch (e) { setResult({ error: e.response?.data?.detail || 'Could not deny.' }); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" style={{ color: BLUE }} /></div>;

  return (
    <div data-testid="tax-reimbursements-tab" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-4">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        Mark a driver-linked expense as reimbursable on the Expenses tab, then push the pending totals onto a payroll run here. Each driver gets one compensation line on their paycheck.
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          Pending Reimbursements <span className="text-slate-400 font-normal">· {money(data.total_pending)}</span>
        </h3>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button data-testid="digest-run-btn" onClick={runDigest} title="Email each driver a recap of approved/reimbursed receipts"
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-md text-sm font-semibold hover:border-[#002FA7] hover:text-[#002FA7]">
              <Mail className="w-4 h-4" /> Email Recap
            </button>
          )}
          {isAdmin && data.groups.length > 0 && (
            <>
            <select data-testid="reimburse-payroll-select" value={payrollId} onChange={(e) => setPayrollId(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm font-semibold focus:ring-2 focus:ring-[#002FA7] focus:outline-none">
              {data.payrolls.length === 0 && <option value="">No payroll runs yet</option>}
              {data.payrolls.map((p) => <option key={p.id} value={p.id}>{p.week_start} → {p.week_end} ({p.driver_count} drivers)</option>)}
            </select>
            <button data-testid="reimburse-pay-btn" onClick={pay} disabled={busy || !payrollId || !(data.total_approved > 0)}
              title={data.total_approved > 0 ? 'Pay approved reimbursements' : 'Approve reimbursements first'}
              className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />} Pay Approved{data.total_approved > 0 ? ` (${money(data.total_approved)})` : ''}
            </button>
            </>
          )}
        </div>
      </div>
      {digestMsg && <div data-testid="digest-msg" className="mb-4 px-4 py-2.5 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800">{digestMsg}</div>}

      {result && result.ok && (
        <div data-testid="reimburse-approve-result" className="mb-4 px-4 py-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800 flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" /> {result.ok}</div>
      )}
      {result && !result.error && result.paid && (
        <div data-testid="reimburse-result" className="mb-4 px-4 py-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
          <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" /> Reimbursed {money(result.total_paid)} across {result.paid.length} driver(s).</div>
          {result.skipped?.length > 0 && <p className="text-xs text-amber-700 mt-1">Skipped: {result.skipped.map((s) => `${s.driver_name} (${s.reason})`).join(', ')}</p>}
        </div>
      )}
      {result?.error && <div className="mb-4 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{result.error}</div>}

      {data.groups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-400">
          No pending reimbursements. Flag a driver expense as reimbursable from the Expenses tab.
        </div>
      ) : (
        <div className="space-y-4">
          {data.groups.map((g) => (
            <div key={g.driver_id} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`reimburse-group-${g.driver_id}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4" style={{ color: BLUE }} />
                  <span className="text-sm font-bold text-slate-800">{g.driver_name || 'Driver'}</span>
                  <span className="text-xs text-slate-400">· {g.count} receipt{g.count !== 1 ? 's' : ''}</span>
                </div>
                <span className="text-sm font-bold text-slate-900 flex items-center gap-3">
                  {isAdmin && g.expenses.some((e) => e.status !== 'approved') && (
                    <button data-testid={`approve-all-${g.driver_id}`} onClick={() => approveDriver(g.driver_id)}
                      className="text-xs font-semibold text-green-700 border border-green-200 bg-green-50 rounded-md px-2 py-1 hover:bg-green-100">Approve all</button>
                  )}
                  {isAdmin && (
                    <button data-testid={`deny-all-${g.driver_id}`} onClick={() => denyDriver(g.driver_id)}
                      className="text-xs font-semibold text-red-700 border border-red-200 bg-red-50 rounded-md px-2 py-1 hover:bg-red-100">Deny all</button>
                  )}
                  <span>{money(g.approved_total || 0)}<span className="text-xs text-slate-400 font-normal"> approved / {money(g.total)}</span></span>
                </span>
              </div>
              <div className="divide-y divide-slate-50">
                {g.expenses.map((e) => {
                  const emptyReceipt = !e.vendor_name && (!e.amount || Number(e.amount) === 0);
                  return (
                  <div key={e.id} data-testid={`reimburse-exp-${e.id}`} className="flex items-center justify-between px-4 py-2 text-sm text-slate-600 gap-3">
                    <span className="min-w-0 truncate">{e.expense_date} · {emptyReceipt ? <span className="italic text-slate-400">Draft receipt (no details entered)</span> : (e.vendor_name || 'Receipt')} <span className="text-slate-400">{e.category_name || ''}</span></span>
                    <div className="flex items-center gap-2 shrink-0">
                      {!emptyReceipt && <span className="font-semibold text-slate-800">{money(e.amount)}</span>}
                      {e.status === 'approved'
                        ? <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Approved</span>
                        : <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Needs Approval</span>}
                      {isAdmin && e.status !== 'approved' && (
                        <button data-testid={`approve-reimburse-${e.id}`} onClick={() => decide(e.id, 'approve')} title="Approve" className="text-green-600 hover:text-green-800"><Check className="w-4 h-4" /></button>
                      )}
                      {isAdmin && (
                        <button data-testid={`deny-reimburse-${e.id}`} onClick={() => decide(e.id, 'deny')} title="Deny" className="text-slate-400 hover:text-red-600"><Ban className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
          {data.payrolls.length === 0 && isAdmin && (
            <p className="text-xs text-amber-700">Generate a payroll run (Payroll page) to push these onto driver paychecks.</p>
          )}
        </div>
      )}

      {/* Denial history — nothing silently disappears */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="denial-history">
        <button data-testid="denial-history-toggle" onClick={() => setShowDenied((s) => !s)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50">
          <span className="flex items-center gap-2 text-sm font-bold text-slate-700" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {showDenied ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Ban className="w-4 h-4 text-slate-400" /> Denial History
            <span className="text-xs text-slate-400 font-normal">· {denied.length} denied</span>
          </span>
        </button>
        {showDenied && (
          <div className="border-t border-slate-100">
            {denied.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">No denied receipts.</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {denied.map((d) => {
                  const emptyReceipt = !d.vendor_name && (!d.amount || Number(d.amount) === 0);
                  return (
                  <div key={d.id} data-testid={`denied-${d.id}`} className="flex items-start justify-between px-4 py-2.5 text-sm gap-3">
                    <div className="min-w-0">
                      <p className="text-slate-800 font-medium truncate">
                        {d.expense_date} · {emptyReceipt ? <span className="italic text-slate-500">Draft receipt (no details entered)</span> : (d.vendor_name || 'Receipt')}
                        {d.driver_name && <span className="text-slate-400 font-normal"> · {d.driver_name}</span>}
                      </p>
                      {d.reason ? <p className="text-xs text-red-600 mt-0.5">Reason: {d.reason}</p> : <p className="text-xs text-slate-400 mt-0.5">No reason given</p>}
                    </div>
                    {!emptyReceipt && <span className="font-semibold text-slate-700 shrink-0">{money(d.amount)}</span>}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxReimbursementsTab;
